// ═══════════════════════════════════════════════════════════════════════════════
// AI Prodavac - Conversation Funnel Component
// Shows conversation state progression
// ═══════════════════════════════════════════════════════════════════════════════

import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatUiNumber, formatUiPercent } from '@/lib/ui-format';

interface FunnelItem {
  state: string;
  count: number;
  percentage: number;
  dropOffRate: number;
}

interface ConversationFunnelProps {
  funnel: FunnelItem[];
}

type FunnelTone = 'info' | 'warning' | 'success' | 'danger' | 'neutral';

const STATE_CONFIG: Record<string, { label: string; tone: FunnelTone }> = {
  greeting: { label: 'Chat Open', tone: 'info' },
  diagnosis: { label: 'Diagnosis', tone: 'info' },
  agitation: { label: '3+ Messages', tone: 'warning' },
  solution_pitch: { label: 'Solution Pitch', tone: 'warning' },
  closing: { label: 'Closing', tone: 'success' },
  lead_capture: { label: 'Lead Form', tone: 'success' },
  downsell: { label: 'Downsell', tone: 'neutral' },
  soft_reject: { label: 'Soft Reject', tone: 'neutral' },
};

const TONE_CLASSES: Record<FunnelTone, { text: string; bar: string }> = {
  info: { text: 'text-status-info', bar: 'bg-status-info/20 border-l-status-info' },
  warning: { text: 'text-status-warning', bar: 'bg-status-warning/20 border-l-status-warning' },
  success: { text: 'text-status-success', bar: 'bg-status-success/20 border-l-status-success' },
  danger: { text: 'text-status-danger', bar: 'bg-status-danger/20 border-l-status-danger' },
  neutral: { text: 'text-foreground', bar: 'bg-surface border-l-border-strong' },
};

export function ConversationFunnel({ funnel }: ConversationFunnelProps) {
  if (funnel.length === 0) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="text-lg">Conversation Funnel</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            No conversation data yet
          </p>
        </CardContent>
      </Card>
    );
  }

  const maxCount = Math.max(...funnel.map(f => f.count), 1);

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="text-lg">Conversation Funnel</CardTitle>
        <p className="text-sm text-muted-foreground">
          Visitor progression through conversation states
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {funnel.map((item, index) => {
            const config = STATE_CONFIG[item.state] || { 
              label: item.state, 
              tone: 'neutral'
            };
            const toneClasses = TONE_CLASSES[config.tone];
            const width = (item.count / maxCount) * 100;
            
            return (
              <div key={item.state}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="relative"
                >
                  <div className="flex items-center justify-between mb-2 px-1">
                    <span className="text-sm font-medium text-foreground">
                      {config.label}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className={cn('text-sm font-bold', toneClasses.text)}>
                        {formatUiNumber(item.count)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        ({formatUiPercent(item.percentage / 100, {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })})
                      </span>
                      {item.dropOffRate > 0 && (
                        <span className="text-xs font-medium text-foreground">
                          -{item.dropOffRate} dropped
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="relative h-10 rounded-lg overflow-hidden bg-surface">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${width}%` }}
                      transition={{ 
                        duration: 0.8, 
                        delay: index * 0.1,
                        ease: "easeOut"
                      }}
                      className={cn(
                        'absolute inset-y-0 left-0 rounded-lg border-l-4',
                        toneClasses.bar
                      )}
                    />
                  </div>
                </motion.div>

                {index < funnel.length - 1 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                    className="flex justify-center my-1.5"
                  >
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
